import { useEffect, useState } from "react";
import {
  makeStyles,
  tokens,
  Field,
  Input,
  Text,
  Spinner,
  Button,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from "@fluentui/react-components";
import { ArrowClockwise24Regular } from "@fluentui/react-icons";
import { getOfficeLocations } from "../api";

const useStyles = makeStyles({
  wrap: { marginTop: "16px", display: "flex", flexDirection: "column", gap: "12px", maxWidth: "720px" },
  intro: { color: tokens.colorNeutralForeground3 },
  sectionHead: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  officeFields: { display: "flex", flexDirection: "column", gap: "10px", padding: "4px 4px 12px 20px" },
  panelHint: { color: tokens.colorNeutralForeground3, fontStyle: "italic", marginBottom: "4px" },
  short: { width: "160px" },
  status: { color: tokens.colorNeutralForeground3, fontStyle: "italic" },
  error: { color: tokens.colorPaletteRedForeground1 },
});

// Every field is set per office location; the "Default (all offices)" entry sets
// the company-wide fallback. These map to the standard signature tokens
// ({{companyName}}, {{addressLine}}, {{brandColor}}, …), resolved from the user's
// officeLocation, and an office inherits the default for any field it leaves blank.
const OFFICE_FIELDS = [
  { key: "companyName", label: "Company name" },
  { key: "addressLine", label: "Address line" },
  { key: "phone", label: "Office phone" },
  { key: "website", label: "Website URL" },
  { key: "websiteLabel", label: "Website label" },
  { key: "logoUrl", label: "Logo URL (hosted HTTPS)" },
  { key: "googleReviewUrl", label: "Google review URL" },
  { key: "googleReviewLabel", label: "Google review label" },
  { key: "brandColor", label: "Brand color", type: "color" },
  { key: "logoWidth", label: "Logo width (px)", type: "number" },
];

const DEFAULT_VALUE = "__default__";

export default function CompanyTab({ value, onChange }) {
  const styles = useStyles();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const offices = value.offices || {};

  // force=true re-scans the directory (Refresh); default uses the server cache.
  const load = (force = false) => {
    setLoading(true);
    setError(null);
    getOfficeLocations(force)
      .then((r) => setLocations(r.locations || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => load(false), []);

  // Show every directory office plus any already-configured office (even if it no
  // longer appears in the directory), sorted and de-duplicated.
  const allOffices = Array.from(new Set([...locations, ...Object.keys(offices)])).sort((a, b) =>
    a.localeCompare(b)
  );

  const coerce = (field, rawVal) =>
    field.type === "number" ? (rawVal === "" ? "" : Number(rawVal) || 0) : rawVal;

  // The default entry writes the company-wide base fields (top level of `value`).
  const setDefaultField = (field, rawVal) => onChange({ ...value, [field.key]: coerce(field, rawVal) });
  const getDefault = (field) => readField(value, field);

  // Each office writes into value.offices[office].
  const setOfficeField = (office, field, rawVal) => {
    const nextOffices = { ...offices, [office]: { ...(offices[office] || {}), [field.key]: coerce(field, rawVal) } };
    onChange({ ...value, offices: nextOffices });
  };
  const getOffice = (office, field) => readField(offices[office] || {}, field);

  const renderFields = (getVal, setVal) => (
    <div className={styles.officeFields}>
      {OFFICE_FIELDS.map((field) => (
        <Field
          key={field.key}
          label={field.label}
          className={field.type === "color" || field.type === "number" ? styles.short : undefined}
        >
          <Input
            type={field.type === "color" ? "color" : field.type === "number" ? "number" : "text"}
            value={getVal(field)}
            onChange={(_, d) => setVal(field, d.value)}
          />
        </Field>
      ))}
    </div>
  );

  return (
    <div className={styles.wrap}>
      <div className={styles.sectionHead}>
        <Text className={styles.intro}>
          Set the values used in signatures per <strong>office location</strong>. Start with{" "}
          <strong>Default (all offices)</strong>, then expand any office to override just what differs for
          the people who work there. A single template fills in automatically based on the user&apos;s
          office location.
        </Text>
        <Button
          appearance="subtle"
          icon={<ArrowClockwise24Regular />}
          onClick={() => load(true)}
          disabled={loading}
          title="Re-scan the directory for office locations"
        >
          Refresh
        </Button>
      </div>

      {loading && <Spinner size="tiny" label="Loading office locations…" />}
      {error && <Text className={styles.error}>Couldn&apos;t load office locations: {error}</Text>}

      <Accordion collapsible multiple defaultOpenItems={[DEFAULT_VALUE]}>
        <AccordionItem value={DEFAULT_VALUE}>
          <AccordionHeader>Default (all offices)</AccordionHeader>
          <AccordionPanel>
            <Text className={styles.panelHint}>
              Company-wide fallback. Any office that leaves a field blank inherits it from here.
            </Text>
            {renderFields(getDefault, setDefaultField)}
          </AccordionPanel>
        </AccordionItem>

        {allOffices.map((office) => (
          <AccordionItem key={office} value={office}>
            <AccordionHeader>{office}</AccordionHeader>
            <AccordionPanel>
              {renderFields(
                (field) => getOffice(office, field),
                (field, rawVal) => setOfficeField(office, field, rawVal)
              )}
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>

      {!loading && !error && allOffices.length === 0 && (
        <Text className={styles.status}>No office locations found in the directory yet.</Text>
      )}
    </div>
  );
}

// Reads a field from a record for display, formatting by type.
function readField(record, field) {
  if (field.type === "color") return record[field.key] || "#00833f";
  if (field.type === "number") return record[field.key] == null ? "" : String(record[field.key]);
  return record[field.key] || "";
}
